import { MutableRefObject, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../ampli';
import NavTitle from '../../../assets/svg/nav-title.svg';
import { marketPricePageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import { useCheckUserLoggedIn } from '../../../hooks/user/useCheckUserLoggedIn';
import { IconButton } from '../buttons/IconButton';
import { Icon } from '../icon/Icon';
import { NakedInput } from '../inputs/NakedInput';
import { BackButton } from './components/BackButton';
import { MarketPriceSearchInputSection } from './components/MarketPriceSearchInputSection';
import { OpenMarketSearchInputSection } from './components/OpenMarketSearchInputSection';

const DEFAULT_MAIN_HEAD_HEIGHT = 48;

export interface SearchInputProps {
  searchKeyword: string;
  setSearchKeyword: (keyword: string) => void;
  inputRef: MutableRefObject<HTMLInputElement | null>;
  isResultPage?: boolean;
  // TODO: 추후 타입 안 받고 범용으로 쓸 수 있는 searchInput 만들고 타입 제거
  searchInputType: 'marketPrice' | 'managedFarm' | 'openMarket';
  placeholder?: string;
  onSearch?: () => void;
}
export interface BackButtonProps {
  isShow: boolean;
  directlyPassedURL?: string;
  onClick?: () => void;
}
interface TextLinkProps {
  text: string;
  path: string;
  onClick?: () => void;
}

interface MainHeadProps {
  hasPluvaLogo?: boolean;
  backButton: BackButtonProps;
  hasMyInfoButton?: boolean;
  headerTitle?: string;
  headerHeight?: number;
  searchInput?: SearchInputProps;
  textLink?: TextLinkProps;
  hasSearchButton?: boolean;
  searchButtonPath?: string;
  favoriteButton?: {
    clickHandler: () => void;
  };
  closeButton?: {
    closeHandler: () => void;
  };
}

export const MainHead = ({
  hasPluvaLogo = false,
  backButton,
  hasMyInfoButton = false,
  headerTitle,
  headerHeight = DEFAULT_MAIN_HEAD_HEIGHT,
  searchInput,
  textLink,
  hasSearchButton,
  searchButtonPath,
  favoriteButton,
  closeButton,
}: MainHeadProps) => {
  const navigate = useNavigate();
  const { isUserLoggedIn } = useCheckUserLoggedIn();

  useEffect(() => {
    if (!searchInput?.inputRef.current) {
      return;
    }

    if (!searchInput?.searchKeyword) {
      searchInput.inputRef.current.value = '';
      return;
    }

    searchInput.inputRef.current.value = searchInput.searchKeyword;
  }, [searchInput?.inputRef, searchInput?.searchKeyword]);

  return (
    <Container headerHeight={headerHeight}>
      <LeftSection>
        {hasPluvaLogo && (
          <PluvaLogoContainer>
            <PluvaLogo src={NavTitle} alt={'Pluva-Market-Icon'} />
          </PluvaLogoContainer>
        )}
        {backButton.isShow && <BackButton backButton={backButton} />}
        {headerTitle && <HeaderTitle>{headerTitle}</HeaderTitle>}
      </LeftSection>

      <RightSection>
        {searchInput && (
          <>
            {searchInput.searchInputType === 'marketPrice' && (
              <MarketPriceSearchInputSection searchInput={searchInput} />
            )}

            {searchInput.searchInputType === 'openMarket' && <OpenMarketSearchInputSection searchInput={searchInput} />}

            {searchInput.searchInputType === 'managedFarm' && (
              <SearchInputBackground>
                <NakedInput
                  type={'text'}
                  placeholder={searchInput.placeholder || ''}
                  inputRef={searchInput.inputRef}
                  textAlign={'left'}
                  isButton
                  isEditable={false}
                  onClick={() => {
                    if (searchInput.onSearch) {
                      searchInput.onSearch();
                    }
                  }}
                />
                <IconButton
                  icon={{ name: 'Search', width: 20, height: 20, style: { fill: '#505363' } }}
                  onClickEvent={() => {
                    if (searchInput.onSearch) {
                      searchInput.onSearch();
                    }
                  }}
                />
              </SearchInputBackground>
            )}
          </>
        )}

        {textLink && (
          <TextLinkText
            onClick={() => {
              if (textLink.onClick) {
                textLink.onClick();
              }
              navigate(textLink.path);
            }}
          >
            {textLink.text}
          </TextLinkText>
        )}
        {hasSearchButton && (
          <SearchInputBackground
            onClick={() => {
              navigate(searchButtonPath || marketPricePageInfo.searchPage.path);
            }}
          >
            <SearchTextWrapper>
              <SearchText>{'브랜드나 모델명으로 검색하기'}</SearchText>
            </SearchTextWrapper>

            <IconButton
              icon={{ name: 'Search', width: 20, height: 20, style: { fill: '#858894' } }}
              onClickEvent={() => {}}
            />
          </SearchInputBackground>
        )}
        {favoriteButton && (
          <FavoriteButton onClick={favoriteButton.clickHandler}>
            <Icon name={'Star'} size={'XLarge'} fill={'#C6C6D5'} />
          </FavoriteButton>
        )}
        {hasMyInfoButton && (
          <MyInfoButton
            onClick={() => {
              ampli.pvmClickHeaderGnbMypageButton();

              if (isUserLoggedIn) {
                navigate('/myinfo');
              } else {
                navigate('/oauth/login');
              }
            }}
          >
            <Icon name={'UserDuotone'} size={'XLarge'} />
          </MyInfoButton>
        )}
        {closeButton && (
          <CloseButton onClick={closeButton.closeHandler}>
            <Icon name={'X'} size={'XLarge'} fill={'#858894'} />
          </CloseButton>
        )}
      </RightSection>
    </Container>
  );
};

const Container = styled.div<{ headerHeight: number }>`
  width: 100%;
  height: ${(props) => props.headerHeight}px;

  padding: 0px 16px;

  display: flex;
  align-items: center;
  flex: 1 0 0;
  align-self: stretch;

  gap: 8px;

  background: var(--Light-Scheme-Monochrome-white, #fff);
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
`;

const PluvaLogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 10px;

  padding-right: 16px;
`;

const PluvaLogo = styled.img`
  width: 155px;
  height: 18px;
`;

const HeaderTitle = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */

  overflow: hidden;
  text-overflow: ellipsis;

  display: block;
  white-space: nowrap;
`;

const RightSection = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
`;

const SearchInputBackground = styled.form`
  height: 40px;

  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;

  padding: 8px;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);

  cursor: pointer;
`;

const SearchTextWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
`;

const SearchText = styled.span`
  display: flex;
  height: 21px;
  max-height: 21px;
  flex-direction: column;
  justify-content: center;
  flex: 1 0 0;

  overflow: hidden;
  color: var(--Light-Scheme-Monochrome-grey70, #a7a9b6);
  text-overflow: ellipsis;
  white-space: nowrap;

  /* Body/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */
`;

const MyInfoButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TextLinkText = styled.button`
  color: var(--Light-Scheme-Color-primary-blue, #196eff);
  text-align: right;

  /* Display/Large/l-medium */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 20.8px */
`;

const SearchButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;

  position: relative;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FavoriteButton = styled.button``;
